import { checkPermission } from 'components/PermissionHide/checkPermission';
import { useEffect, useCallback, useState } from 'react';
import { useObservable } from 'react-use-observable';
import { customerFactory } from 'v2/services/customer';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import type { IAccessPolicyPermissionsAction } from 'v2/interfaces/account';
import type { User } from 'v2/interfaces/user';

type Chats = 'chatBlack' | 'managerAccount';

type ChatConfig = {
  department: number;
  theme: number;
  survey: number;
};

type ChatsConfig = {
  [key in Chats]: ChatConfig;
};

const chatsConfig: ChatsConfig = {
  chatBlack: {
    department: 7,
    theme: 5,
    survey: 4,
  },
  managerAccount: {
    department: 4,
    theme: 4,
    survey: 0,
  },
};

const useChat = () => {
  const [userDidSeeChat, setUserDidSeeChat] = useState(false);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [originalUser] = useObservable(() => eduzzAccountsService.getOriginalUser(), []);
  const [permissions] = useObservable(() => eduzzAccountsService.getPermissions(), []);
  const [hasCoproductionGains] = useObservable(() => customerFactory.checkIfUserHasCoproductionGains(), []);

  const setupChat = useCallback((user: Partial<User>, originalUser: Partial<User>) => {
    LHCChatOptions.attr = [];
    LHCChatOptions.attr.push({ name: 'ID', value: user.id, type: 'hidden' });
    LHCChatOptions.attr.push({ name: 'Email', value: user.email, type: 'hidden' });
    LHCChatOptions.attr.push({ name: 'Nome', value: user.name, type: 'hidden' });

    if (user.id !== originalUser.id) {
      LHCChatOptions.attr.push({ name: 'ID logado', value: originalUser.id, type: 'hidden' });
      LHCChatOptions.attr.push({ name: 'Email logado', value: originalUser.email, type: 'hidden' });
      LHCChatOptions.attr.push({ name: 'Nome logado', value: originalUser.name, type: 'hidden' });
    }

    LHCChatOptions.attr_prefill = [];
    LHCChatOptions.attr_prefill.push({ name: 'email', value: user.email });
    LHCChatOptions.attr_prefill.push({ name: 'username', value: user.name });

    LHCChatOptions.opt = {
      widget_height: 400,
      widget_width: 340,
      popup_height: 520,
      popup_width: 500,
    };
  }, []);

  const getRefferer = () => (document.referrer ? encodeURIComponent(document.referrer.substr(document.referrer.indexOf('://') + 1)) : '');

  const getLocation = () => (document.location ? encodeURIComponent(window.location.href.substring(window.location.protocol.length)) : '');

  const setChatConfig = useCallback(() => {
    const producerHasChatBlack = eduzzAccountsService.isBlackBeltOrMore() || !!user.has_chat_black;
    if (producerHasChatBlack) {
      const { department, survey, theme } = chatsConfig.chatBlack;
      departmentChat = department;
      surveyChat = survey;
      themeChat = theme;
      return;
    }
    if (!!user.account_manager_id) {
      const { department, survey, theme } = chatsConfig.managerAccount;
      departmentChat = department;
      surveyChat = survey;
      themeChat = theme;
    }
  }, [user]);

  const initializeDOM = useCallback(() => {
    setChatConfig();

    const refferer = getRefferer();
    const location = getLocation();

    const po = document.createElement('script');

    po.type = 'text/javascript';
    po.id = 'chat_help';
    po.async = true;

    // eslint-disable-next-line max-len
    po.src = `https://chat.eduzz.com/index.php/por/chat/getstatus/(click)/internal/(position)/bottom_left/(ma)/br/(top)/350/(units)/pixels/(leaveamessage)/true/(department)/${departmentChat}/(theme)/${themeChat}`;

    if (surveyChat > 0) {
      po.src = po.src + `/(survey)/${surveyChat}`;
    }

    po.src = po.src + `?r=${refferer}&l=${location};`;

    const s = document.getElementsByTagName('script')[0];
    s.parentNode.insertBefore(po, s);
  }, [setChatConfig]);

  const checkIfUserCanSeeChat = useCallback((user: User, permissions: IAccessPolicyPermissionsAction, hasCoproductionGains: boolean) => {
    const producerIsInClubeBlack = !!user.is_clube_black;

    if (producerIsInClubeBlack) {
      return false;
    }

    if (!user.general_term) {
      return false;
    }

    if (!user.coproducer && !user.producer && !user.affiliate) {
      return false;
    }

    if (permissions.owned && user.coproducer && !hasCoproductionGains) {
      if (user.producer || user.affiliate) {
        return true;
      }

      return false;
    }

    if (!permissions.owned) {
      const canAccess: boolean = checkPermission(permissions, 'myeduzz:ajuda:chat-vip');

      if (!canAccess) {
        return false;
      }
    }

    return true;
  }, []);

  const truncateName = (name: string) => (name?.length > 45 ? `${name.substring(1, 40)}...` : name);

  const chatStart = useCallback(() => {
    const userCantSeeChat = !checkIfUserCanSeeChat(user, permissions, hasCoproductionGains);

    if (userCantSeeChat) {
      return;
    }

    const userInfosToShowOnChat = {
      id: user.id,
      name: truncateName(user.name),
      email: user.email,
    };

    const originalUserInfosToShowOnChat = {
      id: originalUser.id,
      name: truncateName(originalUser.name),
      email: originalUser.email,
    };

    setUserDidSeeChat(true);
    setupChat(userInfosToShowOnChat, originalUserInfosToShowOnChat);
    initializeDOM();
  }, [user, checkIfUserCanSeeChat, permissions, hasCoproductionGains, originalUser, setupChat, initializeDOM]);

  const chatKill = useCallback(() => {
    if (userDidSeeChat) {
      const script = document.getElementById('chat_help');
      script?.parentElement.removeChild(script);
      const view = document.getElementById('lhc_status_container');
      view?.parentElement.removeChild(view);
      const chat = document.getElementById('lhc_container');
      chat?.parentElement.removeChild(chat);
    }
  }, [userDidSeeChat]);

  useEffect(() => {
    if (user && originalUser) {
      chatStart();
    }

    return () => chatKill();
  }, [chatKill, chatStart, originalUser, user]);
};

export default useChat;
